import React from "react";
import {
  Smartphone, Wifi, Battery, ChevronLeft, Camera, MapPin, Mic,
  CheckCircle2, AlertTriangle, FileText,
} from "lucide-react";

/**
 * MobileFieldShowcase — phone-frame mockup section that demonstrates
 * the field-first UX of ForgedOps. Two phones side-by-side:
 *  1. Equipment Pre-Op (Operator) — checklist, photo enforce, FAIL flow
 *  2. Daily Report (Foreman) — auto-fill chips, simple field layout
 *
 * Pure visual — no API calls. Drops into DemoLanding as section 06.
 */
export default function MobileFieldShowcase() {
  return (
    <section
      className="border-t"
      style={{ borderColor: "#1A1A1A", background: "#0A0A0A" }}
      data-testid="mobile-showcase"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12 sm:py-16">
        <div className="mb-8">
          <div className="font-mono text-[11px] uppercase tracking-[0.4em] mb-2" style={{ color: "#FF6A00" }}>
            06 · BUILT FOR THE TRUCK CAB
          </div>
          <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tight max-w-3xl">
            If it doesn't work in gloves on a rough bid,{" "}
            <span style={{ color: "#FF6A00" }}>it doesn't work.</span>
          </h2>
          <p className="text-sm mt-3 max-w-2xl" style={{ color: "#BDBDBD" }}>
            ForgedOps was field-tested in heavy civil before it was a SaaS
            product. One-handed flows, GPS auto-fill, photo enforcement,
            offline-friendly, no typing what the system already knows.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">
          <div>
            <Phone tagline="OPERATOR · PRE-OP" lock="EX-210 · CAT 336" testId="phone-preop">
              <PhonePreOp />
            </Phone>
            <FieldNote
              icon={Camera}
              title="Photo enforcement on every FAIL."
              copy="A failed checklist row blocks submit until the operator captures evidence. No more 'I'll get to it later'."
            />
          </div>
          <div>
            <Phone tagline="FOREMAN · DAILY REPORT" lock="FO-1003 · I-95 MOT" testId="phone-daily">
              <PhoneDaily />
            </Phone>
            <FieldNote
              icon={MapPin}
              title="GPS + weather auto-load on tap."
              copy="One button captures location and pulls today's weather. No manual entry, no excuses, no fudged data."
            />
          </div>
        </div>

        <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-px" style={{ background: "#1A1A1A" }}>
          {[
            ["Offline-friendly", "Submits queue & sync when signal returns"],
            ["One-handed flows", "Big tap targets, no fiddly inputs"],
            ["QR access",       "Scan to onboard, sign, or open a lesson"],
            ["Bilingual native", "EN / ES toggle persists per crew member"],
          ].map(([t, c]) => (
            <div key={t} className="p-4" style={{ background: "#0F0F0F" }}>
              <div className="font-display font-black text-white text-sm mb-1">{t}</div>
              <div className="text-xs" style={{ color: "#BDBDBD" }}>{c}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Phone({ children, tagline, lock, testId }) {
  return (
    <div className="relative mx-auto" style={{ maxWidth: 340 }} data-testid={testId}>
      {/* Glow */}
      <div className="absolute -inset-6 rounded-[42px] pointer-events-none" style={{
        background: "radial-gradient(closest-side, rgba(255,106,0,0.18), transparent 70%)",
        filter: "blur(20px)",
      }} />
      {/* Phone bezel */}
      <div
        className="relative rounded-[38px] p-2.5 shadow-2xl"
        style={{ background: "#0F0F0F", border: "1px solid #2A2A2A" }}
      >
        <div className="rounded-[30px] overflow-hidden" style={{ background: "#0A0A0A", border: "1px solid #1A1A1A" }}>
          {/* Status bar */}
          <div className="flex items-center justify-between px-5 py-2 text-[10px] font-mono" style={{ background: "#0B0B0B", color: "#BDBDBD" }}>
            <span className="font-bold text-white">9:42</span>
            <div className="flex items-center gap-1.5">
              <Wifi className="w-3 h-3" />
              <Battery className="w-3.5 h-3.5" />
            </div>
          </div>
          {/* App header */}
          <div className="px-4 py-3 flex items-center gap-2 border-b" style={{ borderColor: "#1A1A1A", background: "#0F0F0F" }}>
            <ChevronLeft className="w-4 h-4 text-white" />
            <div className="flex-1 min-w-0">
              <div className="font-mono text-[9px] uppercase tracking-[0.25em]" style={{ color: "#FF6A00" }}>{tagline}</div>
              <div className="text-[11px] font-bold text-white truncate">{lock}</div>
            </div>
            <span className="font-mono text-[9px] uppercase tracking-[0.2em] px-1.5 py-0.5 rounded" style={{ background: "rgba(46,125,50,0.18)", color: "#65DA76", border: "1px solid rgba(46,125,50,0.45)" }}>
              LIVE
            </span>
          </div>
          {/* Body */}
          <div className="p-4 space-y-3" style={{ background: "#0A0A0A" }}>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

function PhonePreOp() {
  return (
    <>
      <div className="rounded-md p-2.5" style={{ background: "#101010", border: "1px solid #1A1A1A" }}>
        <div className="font-mono text-[9px] uppercase tracking-[0.2em]" style={{ color: "#FF6A00" }}>NO EXCUSES MODE</div>
        <div className="flex flex-wrap gap-1 mt-1.5">
          {["Unit · prefilled", "Op · prefilled", "GPS · auto"].map(c => (
            <span key={c} className="font-mono text-[8px] uppercase tracking-[0.15em] px-1.5 py-0.5 rounded text-white" style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)" }}>
              {c}
            </span>
          ))}
        </div>
      </div>
      <PreOpRow label="Engine oil"      status="pass" />
      <PreOpRow label="Coolant"          status="pass" />
      <PreOpRow label="Hydraulic hoses" status="pass" />
      <PreOpRow label="Brakes"           status="fail" />
      <PreOpRow label="Backup alarm"    status="pass" />
      <div className="rounded-md p-2.5 mt-1" style={{ background: "rgba(211,47,47,0.1)", border: "1px solid rgba(211,47,47,0.45)" }}>
        <div className="flex items-center gap-2 mb-1">
          <AlertTriangle className="w-3.5 h-3.5" style={{ color: "#FF8A8A" }} />
          <div className="font-mono text-[9px] uppercase tracking-[0.25em]" style={{ color: "#FF8A8A" }}>FAIL · UNIT LOCKED OUT</div>
        </div>
        <div className="text-[10px] leading-snug" style={{ color: "#FFC9C9" }}>
          1 critical failure detected. Capture photo to submit. Routes to Shop Queue + PM + Safety.
        </div>
      </div>
      <button className="w-full py-2.5 rounded-md font-mono text-[10px] uppercase tracking-[0.25em] font-bold flex items-center justify-center gap-2" style={{ background: "#D32F2F", color: "#FFF" }}>
        <Camera className="w-3.5 h-3.5" /> Capture Photo · Submit
      </button>
    </>
  );
}

function PreOpRow({ label, status }) {
  const pass = status === "pass";
  return (
    <div className="flex items-center justify-between py-1.5 px-1 border-b" style={{ borderColor: "#1A1A1A" }}>
      <span className="text-[11px] text-white">{label}</span>
      <span className="font-mono text-[9px] uppercase tracking-[0.2em] px-2 py-0.5 rounded" style={{
        background: pass ? "rgba(46,125,50,0.2)" : "rgba(211,47,47,0.2)",
        color: pass ? "#65DA76" : "#FF8A8A",
        border: pass ? "1px solid rgba(46,125,50,0.45)" : "1px solid rgba(211,47,47,0.45)",
      }}>
        {status.toUpperCase()}
      </span>
    </div>
  );
}

function PhoneDaily() {
  return (
    <>
      <div className="rounded-md p-2.5" style={{ background: "#101010", border: "1px solid #1A1A1A" }}>
        <div className="font-mono text-[9px] uppercase tracking-[0.2em]" style={{ color: "#FF6A00" }}>NO EXCUSES MODE</div>
        <div className="flex flex-wrap gap-1 mt-1.5">
          {["Job · auto", "PM · auto-route", "Weather · 72°F", "GPS"].map(c => (
            <span key={c} className="font-mono text-[8px] uppercase tracking-[0.15em] px-1.5 py-0.5 rounded text-white" style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)" }}>
              {c}
            </span>
          ))}
        </div>
      </div>
      <Field label="Foreman" value="Demo Foreman · Crew 3" />
      <Field label="Crew size" value="6" />
      <Field label="Labor hrs" value="48.5" />
      <div>
        <div className="font-mono text-[9px] uppercase tracking-[0.25em] mb-1" style={{ color: "#FF6A00" }}>WORK PERFORMED</div>
        <div className="rounded-md p-2 text-[11px] leading-snug" style={{ background: "#101010", border: "1px solid #1A1A1A", color: "#E5E5E5" }}>
          Placed 8" asphalt binder · 320 tons · 5 passes vibratory roller · MOT
          shifted to NB lane.
        </div>
      </div>
      <div className="grid grid-cols-3 gap-1.5">
        <button className="py-1.5 rounded font-mono text-[9px] uppercase tracking-[0.15em] flex items-center justify-center gap-1 font-bold" style={{ background: "#1A1A1A", color: "#FF6A00", border: "1px solid #2A2A2A" }}>
          <Camera className="w-3 h-3" /> Photo
        </button>
        <button className="py-1.5 rounded font-mono text-[9px] uppercase tracking-[0.15em] flex items-center justify-center gap-1 font-bold" style={{ background: "#1A1A1A", color: "#FF6A00", border: "1px solid #2A2A2A" }}>
          <Mic className="w-3 h-3" /> Voice
        </button>
        <button className="py-1.5 rounded font-mono text-[9px] uppercase tracking-[0.15em] flex items-center justify-center gap-1 font-bold" style={{ background: "#1A1A1A", color: "#FF6A00", border: "1px solid #2A2A2A" }}>
          <FileText className="w-3 h-3" /> Sign
        </button>
      </div>
      <button className="w-full py-2.5 rounded-md font-mono text-[10px] uppercase tracking-[0.25em] font-bold flex items-center justify-center gap-2" style={{ background: "#FF6A00", color: "#000" }}>
        <CheckCircle2 className="w-3.5 h-3.5" /> Submit · auto-route to PM
      </button>
    </>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="font-mono text-[9px] uppercase tracking-[0.25em] mb-0.5" style={{ color: "#BDBDBD" }}>{label}</div>
      <div className="rounded-md px-2 py-1.5 text-[11px] text-white" style={{ background: "#101010", border: "1px solid #1A1A1A" }}>{value}</div>
    </div>
  );
}

function FieldNote({ icon: Icon, title, copy }) {
  return (
    <div className="mt-6 p-4 rounded-lg border" style={{ background: "#0F0F0F", borderColor: "#1A1A1A" }}>
      <div className="flex items-start gap-3">
        <span className="inline-flex items-center justify-center w-9 h-9 rounded-md shrink-0" style={{ background: "rgba(255,106,0,0.12)", color: "#FF6A00", border: "1px solid rgba(255,106,0,0.3)" }}>
          <Icon className="w-4 h-4" />
        </span>
        <div>
          <div className="font-display font-black text-white text-sm mb-1">{title}</div>
          <div className="text-xs leading-relaxed" style={{ color: "#BDBDBD" }}>{copy}</div>
        </div>
      </div>
    </div>
  );
}
